import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { nom: "", email: "", telephone: "", type_bien: "", localisation: "", service: "", message: "" };

export const QuoteForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSent(true);
      setForm(initial);
      toast.success(t.quote.toastOk);
    } catch {
      toast.error(t.quote.toastErr);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" className="bg-[#FAFAFA] py-24 md:py-32" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-5"
        >
          <p className="font-script text-[#C9A03C] text-2xl mb-4">{t.quote.eyebrow}</p>
          <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-logo-gradient pb-1 mb-8">
            {t.quote.title}
          </h2>
          <p className="text-[#0A192F]/60 font-light leading-relaxed mb-10">{t.quote.intro}</p>
          <div className="space-y-4 text-[#0A192F]/80">
            <a href="tel:0766230145" className="block hover:text-[#C9A03C] transition-colors duration-300" data-testid="contact-phone">
              <span className="font-serif-lux text-2xl">07 66 23 01 45</span>
            </a>
            <a href="mailto:contact@shamar-services.com" className="block text-sm tracking-wide hover:text-[#C9A03C] transition-colors duration-300" data-testid="contact-email">
              contact@shamar-services.com
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-6 md:col-start-7 bg-white border border-black/5 p-8 md:p-12"
          data-testid="quote-form"
        >
          {sent ? (
            <div className="h-full flex flex-col justify-center items-start py-12" data-testid="quote-success">
              <p className="font-script text-[#C9A03C] text-3xl mb-4">{t.quote.successTitle}</p>
              <p className="text-[#0A192F]/70 font-light leading-relaxed mb-8">{t.quote.successText}</p>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="text-[13px] tracking-[0.16em] uppercase text-[#0A192F] border-b border-[#C9A03C] pb-1 hover:text-[#C9A03C] transition-colors duration-300"
                data-testid="quote-new-request"
              >
                {t.quote.newRequest}
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <input required className="lux-input" placeholder={t.quote.nom} value={form.nom} onChange={set("nom")} data-testid="quote-input-nom" />
              </div>
              <div>
                <input required type="email" className="lux-input" placeholder={t.quote.email} value={form.email} onChange={set("email")} data-testid="quote-input-email" />
              </div>
              <div>
                <input className="lux-input" placeholder={t.quote.tel} value={form.telephone} onChange={set("telephone")} data-testid="quote-input-telephone" />
              </div>
              <div>
                <select required className="lux-input bg-transparent" value={form.type_bien} onChange={set("type_bien")} data-testid="quote-select-type-bien">
                  <option value="" disabled>{t.quote.typeBien}</option>
                  {Object.entries(t.quote.typeOptions).map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <select required className="lux-input bg-transparent" value={form.localisation} onChange={set("localisation")} data-testid="quote-select-localisation">
                  <option value="" disabled>{t.quote.localisation}</option>
                  {Object.entries(t.quote.locOptions).map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <select required className="lux-input bg-transparent" value={form.service} onChange={set("service")} data-testid="quote-select-service">
                  <option value="" disabled>{t.quote.service}</option>
                  {Object.entries(t.quote.serviceOptions).map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <textarea rows={4} className="lux-input resize-none" placeholder={t.quote.message} value={form.message} onChange={set("message")} data-testid="quote-input-message" />
              </div>
              <div className="sm:col-span-2 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-10 py-4 bg-[#0A192F] text-[#F0F0F0] text-[13px] tracking-[0.16em] uppercase hover:bg-[#C9A03C] hover:text-[#050B14] transition-colors duration-400 disabled:opacity-50"
                  data-testid="quote-submit-button"
                >
                  {loading ? t.quote.sending : t.quote.submit}
                </button>
              </div>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
};
